import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

export interface AppConfig {
    blApiToken: string;
    company_id: string;
    customer_name: string;
    apiBaseUrl: string;
}

@Injectable({
    providedIn: 'root',
})
export class FormSchemaApiService {

    private http = inject(HttpClient);

    async searchVenue(company: any, query: string, appConfig: AppConfig) {
        try {
            const headers = {
                'Content-Type': 'application/json',
                'x-company-id': `${company.id}`,
                'clienttoken': `${appConfig.blApiToken}`
            };
            return await this.http.get(`${appConfig.apiBaseUrl}/company/${company}/venue-lookup?search=${query.toLowerCase()}`, { headers }).toPromise();
        } catch (error) {
            return (error as Error).message;
        }
    }

    /** Gets every venue for the client. The API pages the list, so it follows `cursor` until it is empty. */
    async getVenues(appConfig: AppConfig): Promise<any[]> {
        const headers = { 'Content-Type': 'text/plain', clienttoken: `${appConfig.blApiToken}` };
        const venues: any[] = [];
        let cursor = '';
        do {
            const url = `${appConfig.apiBaseUrl}/venues/addvenue?size=150${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`;
            const res: any = await firstValueFrom(this.http.post(url, JSON.stringify({ function: 'getVenues' }), { headers }));
            venues.push(...(Array.isArray(res?.data) ? res.data : []));
            cursor = res?.cursor || '';
        } while (cursor);
        return venues;
    }

    async getFormSchema(formId: string, appConfig: AppConfig) {
        try {
            const headers = {
                'Content-Type': 'application/json',
                clienttoken: `${appConfig.blApiToken}`,
            };
            const data = {
                company: `${appConfig.company_id}`,
                client: `${appConfig.customer_name}`,
                form_name: `${formId}`,
            };
            return await this.http
                .post(`${appConfig.apiBaseUrl}/form/schema`, data, { headers })
                .toPromise();
        } catch (error) {
            return (error as Error).message;
        }
    }

    async getAddressLookup(postcode: string, appConfig: AppConfig) {
        try {
            const headers = {
                'Content-Type': 'application/json',
                clienttoken: `${appConfig.blApiToken}`,
            };
            return await this.http
                .get(`${appConfig.apiBaseUrl}/ordnance_survey/postcode/${postcode}`, { headers })
                .toPromise();

        }
        catch (error) {
            return (error as Error).message;
        }
    }

    async getExtraItemProperties(bookingQuestionCode: string, companyId: string, serviceId: string, appConfig: AppConfig) {
        try {
            const headers = {
                'Content-Type': 'application/json',
                clienttoken: `${appConfig.blApiToken}`,
            };

            return await this.http
                .get(
                    `${appConfig.apiBaseUrl}/company/${companyId}/extra-item/${bookingQuestionCode}/service/${serviceId}`,
                    { headers }
                )
                .toPromise();
        } catch (error) {
            return (error as Error).message;
        }
    }

    async awsLookup(
        appConfig: AppConfig,
        table: string,
        field: string,
        reference: string,
        referenceExtra?: string,
        fieldExtra?: string
    ): Promise<any> {
        try {
            const headers = {
                'Content-Type': 'application/json',
                clienttoken: `${appConfig.blApiToken}`,
            };

            const bodyData: Record<string, string> = {
                client: `${appConfig.customer_name}`,
                reference: `${reference}`,
                table: `${table}`,
                field: `${field}`
            };

            if (referenceExtra && fieldExtra) {
                bodyData['referenceExtra'] = fieldExtra;
                bodyData['field_extra'] = referenceExtra;
            }

            return await this.http
                .post(`${appConfig.apiBaseUrl}/eligibility_reference_lookup`, bodyData, { headers })
                .toPromise();
        } catch (error) {
            return { error: true, message: (error as Error).message };
        }
    }

}